import { Repeat, UserPlus, MapPin, Smartphone, Tablet, Monitor } from "lucide-react";

const ctrOf = (x) => (x.impressions > 0 ? Number(((x.clicks / x.impressions) * 100).toFixed(2)) : 0);

const GROUPS = {
  back: { label: "Returning people", desc: "Already visited your site, location or engaged with you", icon: Repeat },
  near: { label: "Near competitors & hotspots", desc: "Phones inside virtual fences around busy, relevant spots", icon: MapPin },
  fresh: { label: "New people", desc: "\"In the market\" based on their interests", icon: UserPlus },
};

// items: [{name, impressions, clicks, verified_visits?}] -> groups for WhoSawIt (+ back/fresh for the callout).
export const audienceGroups = (items, { splitNear = false } = {}) => {
  const buckets = { back: { impressions: 0, clicks: 0, visits: 0 }, near: { impressions: 0, clicks: 0, visits: 0 }, fresh: { impressions: 0, clicks: 0, visits: 0 } };
  items.forEach((i) => {
    const n = i.name || "";
    const key = /retarget/i.test(n) ? "back" : splitNear && /geo.?fenc|conquest|competitor|hotspot/i.test(n) ? "near" : "fresh";
    buckets[key].impressions += i.impressions || 0;
    buckets[key].clicks += i.clicks || 0;
    buckets[key].visits += i.verified_visits || 0;
  });
  const total = Object.values(buckets).reduce((s, b) => s + b.impressions, 0);
  if (!total) return null;
  const groups = ["back", "near", "fresh"]
    .filter((k) => buckets[k].impressions > 0)
    .map((k) => ({
      key: k, ...GROUPS[k], ...buckets[k], ctr: ctrOf(buckets[k]), pct: (buckets[k].impressions / total) * 100,
      extra: buckets[k].visits > 0 ? `${buckets[k].visits.toLocaleString()} verified walk-ins` : null,
    }));
  if (groups.length < 2) return null;
  const back = groups.find((g) => g.key === "back");
  const others = groups.filter((g) => g.key !== "back");
  const rest = others.reduce((s, g) => ({ impressions: s.impressions + g.impressions, clicks: s.clicks + g.clicks }), { impressions: 0, clicks: 0 });
  return { groups, back, fresh: { ...rest, ctr: ctrOf(rest) } };
};

export const audienceStory = (back, fresh) => {
  if (!back || !fresh.impressions) return null;
  return back.ctr > fresh.ctr
    ? `Returning people clicked at ${back.ctr}% vs ${fresh.ctr}% for everyone else. That makes sense: people who already know your brand are more likely to come back.`
    : `People seeing you for the first time clicked at ${fresh.ctr}% vs ${back.ctr}% for returning people. Your ads are winning over people who are just discovering you.`;
};

const DEVICE_ICON = { Mobile: Smartphone, Tablet, Desktop: Monitor };

// devices: [{device, impressions}] -> Bar rows sorted by share.
export const deviceRows = (devices) => {
  const list = devices.filter((d) => d.impressions > 0).sort((a, b) => b.impressions - a.impressions);
  const total = list.reduce((s, d) => s + d.impressions, 0);
  return {
    total,
    rows: list.map((d) => ({ label: d.device, icon: DEVICE_ICON[d.device], pct: (d.impressions / total) * 100, right: `${((d.impressions / total) * 100).toFixed(1)}%` })),
  };
};

export const deviceGapNote = (headlineImpressions, deviceTotal) =>
  deviceTotal > 0 && headlineImpressions - deviceTotal > 0
    ? `${(headlineImpressions - deviceTotal).toLocaleString()} views had no device type recorded by the ad exchanges.`
    : null;
